import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import bs58 from 'bs58';
import {
  Card,
  CardContent,
  Typography,
  Box,
  IconButton,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Divider
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useAccount, useSignMessage } from 'wagmi';
import { ConnectButton } from '@rainbow-me/rainbowkit';
import TokenLogo from './TokenLogo';
import { formatDateTime } from '../utils/time';

const StyledCard = styled(Card)(({ theme }) => ({
  maxWidth: 520,
  margin: '2rem auto',
  position: 'relative',
  minHeight: 420,
  transition: 'all 0.3s ease',
  boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
  borderRadius: '16px',
  background: 'linear-gradient(to bottom, #ffffff, #fafafa)'
}));

const PageTitle = styled(Typography)(({ theme }) => ({
  textAlign: 'center',
  fontSize: '1.5rem',
  fontWeight: 600,
  color: theme.palette.text.primary,
  padding: theme.spacing(3),
  borderBottom: '1px solid #eaeaea',
}));

const LogoWrapper = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(2),
  right: theme.spacing(2),
  width: '40px',
  height: '40px',
  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'contain'
  }
}));

const PaymentsList = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(3),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1.5)
}));

const PaymentItem = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.8, 2),
  borderRadius: 16,
  border: '1px solid rgba(0, 0, 0, 0.08)',
  backgroundColor: '#fff',
  boxShadow: '0 2px 10px rgba(0, 0, 0, 0.04)'
}));

const StatusChip = styled(Chip)(({ theme }) => ({
  fontSize: '0.7rem',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.5px'
}));

const NETWORK_LABELS = {
  ethereum: 'Ethereum',
  base: 'Base',
  bsc: 'BNB Chain',
  solana: 'Solana',
  avalanche: 'Avalanche',
  arbitrum: 'Arbitrum',
  optimism: 'Optimism',
  ton: 'TON',
  sui: 'Sui'
};

const detectWalletType = (address) => {
  if (!address) return null;
  if (/^0x[a-fA-F0-9]{40}$/.test(address)) return 'ethereum';
  if (/^[1-9A-HJ-NP-Za-km-z]{32,44}$/.test(address)) return 'solana';
  if (/^0x[a-fA-F0-9]{64}$/.test(address)) return 'sui';
  return null;
};

const formatAmount = (value, options = {}) => {
  if (value === null || value === undefined || value === '') return '--';
  const numberValue = Number.parseFloat(value);
  if (!Number.isFinite(numberValue)) return '--';
  const maximumFractionDigits = options.maximumFractionDigits ?? 6;
  return numberValue.toLocaleString(undefined, { maximumFractionDigits });
};

const formatUsd = (value) => {
  if (value === null || value === undefined || value === '') return '--';
  const numberValue = Number.parseFloat(value);
  if (!Number.isFinite(numberValue)) return '--';
  return `$${numberValue.toFixed(2)}`;
};

const formatNetworkLabel = (network) => {
  if (!network) return 'Unknown';
  return NETWORK_LABELS[network] || network.toUpperCase();
};

const getStatusColor = (status) => {
  const normalized = String(status || '').toLowerCase();
  if (normalized === 'confirmed' || normalized === 'paid') return 'success';
  if (normalized === 'underpaid') return 'warning';
  if (normalized === 'pending') return 'default';
  return 'default';
};

const ViewPayments = ({ onBack }) => {
  const { address: evmAddress, isConnected } = useAccount();
  const { signMessageAsync } = useSignMessage();
  const [walletAddress, setWalletAddress] = useState('');
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [lastQueried, setLastQueried] = useState('');

  const trimmedAddress = walletAddress.trim();
  const detectedType = useMemo(
    () => detectWalletType(trimmedAddress),
    [trimmedAddress]
  );

  useEffect(() => {
    if (!walletAddress && evmAddress) {
      setWalletAddress(evmAddress);
    }
  }, [evmAddress, walletAddress]);

  const resolveSignature = async (message, walletType) => {
    if (walletType === 'ethereum') {
      if (!isConnected || !evmAddress) {
        throw new Error('Connect an EVM wallet to sign this request.');
      }
      if (trimmedAddress && evmAddress.toLowerCase() !== trimmedAddress.toLowerCase()) {
        throw new Error('Connected wallet does not match the entered address.');
      }
      return await signMessageAsync({ message });
    }

    if (walletType === 'solana') {
      const provider = window.solana;
      if (!provider?.signMessage) {
        throw new Error('Solana wallet not detected or message signing unavailable.');
      }
      if (!provider.isConnected) {
        await provider.connect();
      }
      const publicKey = provider.publicKey?.toString();
      if (trimmedAddress && publicKey && publicKey !== trimmedAddress) {
        throw new Error('Connected Solana wallet does not match the entered address.');
      }
      const encoded = new TextEncoder().encode(message);
      const signed = await provider.signMessage(encoded, 'utf8');
      return bs58.encode(signed.signature);
    }

    throw new Error('Unsupported wallet address format.');
  };

  const handleFetchPayments = async () => {
    setError('');
    setPayments([]);

    const addressToUse = trimmedAddress || evmAddress || '';
    if (!addressToUse) {
      setError('Enter a wallet address to continue.');
      return;
    }

    const walletType = detectWalletType(addressToUse);
    if (!walletType) {
      setError('Unsupported wallet address format.');
      return;
    }

    setLoading(true);
    try {
      const challengeResponse = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/v1/payor/wallet/challenge`,
        {
          wallet_address: addressToUse,
          wallet_type: walletType
        }
      );

      const challengeId = challengeResponse.data?.challenge_id;
      const message = challengeResponse.data?.message;
      const resolvedWalletType = challengeResponse.data?.wallet_type || walletType;

      if (!challengeId || !message) {
        throw new Error('Failed to create a signature challenge.');
      }

      const signature = await resolveSignature(message, resolvedWalletType);

      const paymentsResponse = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/v1/payor/payments`,
        {
          wallet_address: addressToUse,
          wallet_type: resolvedWalletType,
          challenge_id: challengeId,
          signature
        }
      );

      const responseData = paymentsResponse.data?.data || [];
      setPayments(responseData);
      setLastQueried(addressToUse);
    } catch (err) {
      const responseError = err?.response?.data?.error || err?.response?.data?.message;
      setError(responseError || err.message || 'Unable to load payments.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <StyledCard>
      {onBack && (
        <IconButton
          onClick={onBack}
          sx={{ position: 'absolute', top: 16, left: 16 }}
        >
          <ArrowBackIcon />
        </IconButton>
      )}

      <LogoWrapper>
        <img src="/pepay-logo-rounded-official.png" alt="PePay Logo" />
      </LogoWrapper>

      <PageTitle>View Payments</PageTitle>

      <CardContent>
        <Box sx={{ mt: 1, px: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Verify your wallet to view recent payment history.
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <ConnectButton chainStatus="none" showBalance={false} />
            {evmAddress && (
              <Button
                size="small"
                variant="outlined"
                onClick={() => setWalletAddress(evmAddress)}
                sx={{ textTransform: 'none', borderRadius: 8 }}
              >
                Use connected wallet
              </Button>
            )}
          </Box>

          <TextField
            fullWidth
            variant="outlined"
            label="Wallet address"
            value={walletAddress}
            onChange={(event) => setWalletAddress(event.target.value)}
            placeholder="0x... or Solana address"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                backgroundColor: '#f8f9fa'
              }
            }}
          />

          {detectedType && (
            <Typography variant="caption" color="text.secondary">
              Detected wallet type: {detectedType === 'ethereum' ? 'EVM' : detectedType}
            </Typography>
          )}

          {error && <Alert severity="error">{error}</Alert>}

          <Button
            variant="contained"
            onClick={handleFetchPayments}
            disabled={loading}
            sx={{
              borderRadius: 12,
              textTransform: 'none',
              fontWeight: 600,
              py: 1.2
            }}
          >
            {loading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={18} color="inherit" />
                Verifying wallet...
              </Box>
            ) : (
              'View Payment History'
            )}
          </Button>
        </Box>

        {lastQueried && !loading && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block', px: 2 }}>
            Showing payments for {lastQueried.slice(0, 6)}...{lastQueried.slice(-4)}
          </Typography>
        )}

        {!loading && payments.length === 0 && lastQueried && !error && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2, px: 2 }}>
            No payments found yet for this wallet.
          </Typography>
        )}

        {payments.length > 0 && (
          <PaymentsList>
            {payments.map((payment) => (
              <PaymentItem key={payment.id}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <TokenLogo
                      uri={payment.token_uri || payment.photo_uri}
                      symbol={payment.token_symbol}
                      size={32}
                    />
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {formatAmount(payment.amount_token)} {payment.token_symbol}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {payment.merchant_name || 'Merchant'} · {formatNetworkLabel(payment.network)}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <StatusChip
                      label={payment.status || 'unknown'}
                      color={getStatusColor(payment.status)}
                      size="small"
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      {formatDateTime(payment.confirmed_at || payment.invoice_created_at)}
                    </Typography>
                  </Box>
                </Box>
                <Divider sx={{ my: 1.5 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Invoice {payment.invoice_id?.slice(0, 8)}...
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatUsd(payment.amount_usd)}
                  </Typography>
                </Box>
              </PaymentItem>
            ))}
          </PaymentsList>
        )}
      </CardContent>
    </StyledCard>
  );
};

export default ViewPayments;
